import React, { useState, useEffect } from 'react';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { getFirestore, collection, getDocs, doc, setDoc, deleteDoc, query, orderBy, serverTimestamp, where } from 'firebase/firestore';
import Swal from 'sweetalert2';
import { useSelectedLocation } from './LocationContext';
import SetupGuard from '../components/SetupGuard';
import AdminLayout from './AdminLayout';

const RECURRENCE_TYPES = [
  { key: 'none', label: 'One-time only' },
  { key: 'weekly', label: 'Weekly (Every 7 days)' },
  { key: 'biweekly', label: 'Bi-weekly (Every 14 days)' },
  { key: 'semimonthly', label: 'Semi-monthly (Twice a month)' },
  { key: 'monthly', label: 'Monthly' },
  { key: 'custom', label: 'Custom (Every X days)' },
];

const STATUS_OPTIONS = [
  { key: 'open', label: 'Open', color: '#28a745' },
  { key: 'processing', label: 'Processing', color: '#ff6b35' },
  { key: 'closed', label: 'Closed', color: '#6c757d' },
];

const SEMI_MONTHLY_OPTIONS = [
  { label: '1st & 15th', firstDay: 1, secondDay: 15 },
  { label: '15th & Last', firstDay: 15, secondDay: 'last' },
  { label: '5th & 20th', firstDay: 5, secondDay: 20 },
  { label: '10th & 25th', firstDay: 10, secondDay: 25 },
];

const PayPeriodsPage = ({ embedded = false }) => {
  const [user, setUser] = useState(null);
  const [periods, setPeriods] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPeriod, setEditingPeriod] = useState(null);
  const [generating, setGenerating] = useState(false);
  // Date range filter defaults to 30 days before/after today
  const addDaysISO = (days) => {
    const d = new Date();
    d.setDate(d.getDate() + days);
    return d.toISOString().split('T')[0];
  };
  const [dateFrom, setDateFrom] = useState(addDaysISO(-30));
  const [dateTo, setDateTo] = useState(addDaysISO(30));
  // Status filter — default only open + processing
  const [showClosed, setShowClosed] = useState(false);
  const [formData, setFormData] = useState({
    payPeriodName: '',
    startDate: '',
    endDate: '',
    payDate: '',
    status: 'open',
    locationId: '',
    recurring: false,
    recurrenceType: 'none',
    recurrenceCount: 4,
    customDays: 14,
    semiMonthlyOption: 0, // index into SEMI_MONTHLY_OPTIONS
  });

  const auth = getAuth();
  const { selectedLocation } = useSelectedLocation() || {};

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        await loadLocations(currentUser.uid);
        await loadPeriods(currentUser.uid);
      }
    });
    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reload when date range changes
  useEffect(() => {
    if (!user) return;
    loadPeriods(user.uid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, dateFrom, dateTo]);

  const loadLocations = async (uid) => {
    try {
      const db = getFirestore();
      const snapshot = await getDocs(collection(db, 'users', uid, 'locations'));
      setLocations(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (e) {
      console.error('Error loading locations:', e);
    }
  };

  const loadPeriods = async (uid) => {
    try {
      
      const db = getFirestore();
      // Query only pay periods whose startDate falls within the selected range
      // This avoids reading all historical documents
      const snapshot = await getDocs(
        query(
          collection(db, 'users', uid, 'payPeriods'),
          where('startDate', '>=', dateFrom),
          where('startDate', '<=', dateTo),
          orderBy('startDate', 'desc')
        )
      );
      let list = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      // Filter by selected location
      console.log(list);
      if (selectedLocation?.id) {
        list = list.filter(p => p.locationId === selectedLocation.id);
      }
      setPeriods(list);
    } catch (e) {
      console.error('Error loading pay periods:', e);
    } finally {
      setLoading(false);
    }
  };

  const formatDateDisplay = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso + 'T00:00:00');
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const getDaysInPeriod = (start, end) => {
    const s = new Date(start + 'T00:00:00');
    const e = new Date(end + 'T00:00:00');
    const diff = Math.round((e - s) / (1000 * 60 * 60 * 24));
    return diff + 1;
  };

  const validateForm = () => {
    if (!formData.payPeriodName.trim()) {
      Swal.fire('Error', 'Pay period name is required', 'error');
      return false;
    }
    if (!formData.startDate || !formData.endDate) {
      Swal.fire('Error', 'Start and end dates are required', 'error');
      return false;
    }
    if (formData.startDate > formData.endDate) {
      Swal.fire('Error', 'Start date must be before end date', 'error');
      return false;
    }
    if (formData.recurring && formData.recurrenceType === 'none') {
      Swal.fire('Error', 'Please select a recurrence type or disable recurring', 'error');
      return false;
    }
    if (formData.recurring && formData.recurrenceType === 'custom' && (!formData.customDays || formData.customDays < 1)) {
      Swal.fire('Error', 'Please enter a valid number of days for custom recurrence', 'error');
      return false;
    }
    if (formData.recurring && (!formData.recurrenceCount || formData.recurrenceCount < 1)) {
      Swal.fire('Error', 'Please enter a valid number of periods to generate', 'error');
      return false;
    }
    return true;
  };

  const addDays = (isoDate, days) => {
    const d = new Date(isoDate + 'T00:00:00');
    d.setDate(d.getDate() + days);
    return d.toISOString().split('T')[0];
  };

  const getLastDayOfMonth = (year, month) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const generatePeriods = async () => {
    if (!validateForm() || !user) return;

    const periodsToCreate = [];
    let currentStart = formData.startDate;
    let currentEnd = formData.endDate;

    const count = formData.recurring ? parseInt(formData.recurrenceCount, 10) : 1;

    for (let i = 0; i < count; i++) {
      periodsToCreate.push({
        payPeriodName: count === 1 ? formData.payPeriodName : `${formData.payPeriodName} #${i + 1}`,
        startDate: currentStart,
        endDate: currentEnd,
        payDate: formData.payDate || addDays(currentEnd, 2),
        status: i === 0 ? formData.status : 'open',
        locationId: formData.locationId || null,
        createdAt: serverTimestamp(),
      });

      if (!formData.recurring) break;

      // Calculate next period
      const type = formData.recurrenceType;
      if (type === 'weekly') {
        currentStart = addDays(currentStart, 7);
        currentEnd = addDays(currentEnd, 7);
      } else if (type === 'biweekly') {
        currentStart = addDays(currentStart, 14);
        currentEnd = addDays(currentEnd, 14);
      } else if (type === 'monthly') {
        const s = new Date(currentStart + 'T00:00:00');
        const e = new Date(currentEnd + 'T00:00:00');
        s.setMonth(s.getMonth() + 1);
        e.setMonth(e.getMonth() + 1);
        currentStart = s.toISOString().split('T')[0];
        currentEnd = e.toISOString().split('T')[0];
      } else if (type === 'semimonthly') {
        const option = SEMI_MONTHLY_OPTIONS[formData.semiMonthlyOption];
        const s = new Date(currentStart + 'T00:00:00');
        // Move to next month
        s.setMonth(s.getMonth() + 1);
        const year = s.getFullYear();
        const month = s.getMonth();
        const lastDay = getLastDayOfMonth(year, month);
        const secondDay = option.secondDay === 'last' ? lastDay : option.secondDay;
        // Generate two periods for next month
        periodsToCreate.push({
          payPeriodName: `${formData.payPeriodName} #${i + 2}`,
          startDate: `${year}-${String(month + 1).padStart(2, '0')}-${String(option.firstDay).padStart(2, '0')}`,
          endDate: `${year}-${String(month + 1).padStart(2, '0')}-${String(secondDay - 1).padStart(2, '0')}`,
          payDate: formData.payDate || addDays(`${year}-${String(month + 1).padStart(2, '0')}-${String(secondDay - 1).padStart(2, '0')}`, 2),
          status: 'open',
          createdAt: serverTimestamp(),
        });
        periodsToCreate.push({
          payPeriodName: `${formData.payPeriodName} #${i + 3}`,
          startDate: `${year}-${String(month + 1).padStart(2, '0')}-${String(secondDay).padStart(2, '0')}`,
          endDate: `${year}-${String(month + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`,
          payDate: formData.payDate || addDays(`${year}-${String(month + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`, 2),
          status: 'open',
          createdAt: serverTimestamp(),
        });
        i += 1; // Skip one iteration since we created 2 extra
      } else if (type === 'custom') {
        const days = parseInt(formData.customDays, 10);
        currentStart = addDays(currentStart, days);
        currentEnd = addDays(currentEnd, days);
      }
    }

    try {
      setGenerating(true);
      const db = getFirestore();
      await Promise.all(
        periodsToCreate.map(p => {
          const docRef = doc(collection(db, 'users', user.uid, 'payPeriods'));
          return setDoc(docRef, p);
        })
      );
      Swal.fire('Success', `${periodsToCreate.length} pay period(s) created!`, 'success');
      setShowModal(false);
      resetForm();
      await loadPeriods(user.uid);
      window.dispatchEvent(new CustomEvent('setupGuideRefresh'));
    } catch (e) {
      console.error('Error creating pay periods:', e);
      Swal.fire('Error', e.message || 'Failed to create pay periods', 'error');
    } finally {
      setGenerating(false);
    }
  };

  const handleSave = async () => {
    if (formData.recurring) {
      await generatePeriods();
      return;
    }
    if (!validateForm() || !user) return;

    try {
      const db = getFirestore();
      const data = {
        payPeriodName: formData.payPeriodName.trim(),
        startDate: formData.startDate,
        endDate: formData.endDate,
        payDate: formData.payDate || null,
        status: formData.status,
        updatedAt: serverTimestamp(),
      };

      if (editingPeriod) {
        await setDoc(doc(db, 'users', user.uid, 'payPeriods', editingPeriod.id), data, { merge: true });
        Swal.fire('Success', 'Pay period updated!', 'success');
      } else {
        data.createdAt = serverTimestamp();
        const docRef = doc(collection(db, 'users', user.uid, 'payPeriods'));
        await setDoc(docRef, data);
        Swal.fire('Success', 'Pay period created!', 'success');
      }

      setShowModal(false);
      resetForm();
      await loadPeriods(user.uid);
      window.dispatchEvent(new CustomEvent('setupGuideRefresh'));
    } catch (e) {
      console.error('Error saving pay period:', e);
      Swal.fire('Error', e.message || 'Failed to save', 'error');
    }
  };

  const handleDelete = async (period) => {
    const result = await Swal.fire({
      title: 'Delete Pay Period?',
      text: `Delete "${period.payPeriodName}" (${period.startDate} to ${period.endDate})?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Delete',
    });
    if (!result.isConfirmed || !user) return;

    try {
      const db = getFirestore();
      await deleteDoc(doc(db, 'users', user.uid, 'payPeriods', period.id));
      Swal.fire('Deleted!', 'Pay period removed.', 'success');
      await loadPeriods(user.uid);
      window.dispatchEvent(new CustomEvent('setupGuideRefresh'));
    } catch (e) {
      console.error('Error deleting pay period:', e);
      Swal.fire('Error', e.message || 'Failed to delete', 'error');
    }
  };

  const handleClosePeriod = async (period) => {
    const result = await Swal.fire({
      title: 'Close Pay Period?',
      text: `Close "${period.payPeriodName}"? No more timesheets can be added.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ff6b35',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Close',
    });
    if (!result.isConfirmed || !user) return;

    try {
      const db = getFirestore();
      await setDoc(doc(db, 'users', user.uid, 'payPeriods', period.id), {
        status: 'closed',
        closedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      }, { merge: true });
      Swal.fire('Closed!', 'Pay period has been closed.', 'success');
      await loadPeriods(user.uid);
      window.dispatchEvent(new CustomEvent('setupGuideRefresh'));
    } catch (e) {
      console.error('Error closing pay period:', e);
      Swal.fire('Error', e.message || 'Failed to close', 'error');
    }
  };

  const resetForm = () => {
    setEditingPeriod(null);
    setFormData({
      payPeriodName: '',
      startDate: '',
      endDate: '',
      payDate: '',
      status: 'open',
      locationId: selectedLocation?.id || (locations.length > 0 ? locations[0].id : ''),
      recurring: false,
      recurrenceType: 'none',
      recurrenceCount: 4,
      customDays: 14,
      semiMonthlyOption: 0,
    });
  };

  const openNew = () => {
    resetForm();
    setShowModal(true);
  };

  const openEdit = (period) => {
    setEditingPeriod(period);
    setFormData({
      payPeriodName: period.payPeriodName || '',
      startDate: period.startDate || '',
      endDate: period.endDate || '',
      payDate: period.payDate || '',
      status: period.status || 'open',
      locationId: period.locationId || '',
      recurring: false,
      recurrenceType: 'none',
      recurrenceCount: 4,
      customDays: 14,
      semiMonthlyOption: 0,
    });
    setShowModal(true);
  };

  const getStatusBadge = (status) => {
    const st = STATUS_OPTIONS.find(s => s.key === status);
    return (
      <span
        className="badge"
        style={{
          backgroundColor: (st?.color || '#6c757d') + '20',
          color: st?.color || '#6c757d',
          fontSize: '0.8rem',
          borderRadius: 8,
        }}
      >
        {st?.label || status}
      </span>
    );
  };

  if (loading) {
    const loader = (
      <div className="text-center py-5">
        <img src="/fodyfee_loading.png" alt="Loading..." style={{ width: 80, height: 80, objectFit: 'contain' }} />
      </div>
    );
    if (embedded) return loader;
    return loader;
  }

  // Apply status filter to displayed periods
  const effectiveStatuses = showClosed ? ['open', 'processing', 'closed'] : ['open', 'processing'];
  const filteredPeriods = periods.filter(p => effectiveStatuses.includes(p.status));

  // Group filtered periods by year for display
  const groupedByYear = filteredPeriods.reduce((acc, p) => {
    const year = p.startDate?.split('-')[0] || 'Unknown';
    if (!acc[year]) acc[year] = [];
    acc[year].push(p);
    return acc;
  }, {});

  const content = (
    <SetupGuard prerequisites={['location']}>
      <>
      <div className="pay-periods-page">
        {/* Header */}
        <div className="row mb-4 align-items-center">
          <div className="col-md-6">
            <h4 className="font-weight-bold">Pay Periods</h4>
            <p className="text-muted">
              {selectedLocation?.id ? (
                <>
                  <span className="badge px-2 py-1" style={{ background: '#ff6b3520', color: '#ff6b35', borderRadius: 8 }}>
                    <i className="fas fa-store mr-1"></i>
                    {selectedLocation.companyName || selectedLocation.locationName}
                  </span>
                  <span className="ml-2">Manage payroll cycles for this location</span>
                </>
              ) : (
                'Manage payroll cycles and timesheet windows'
              )}
            </p>
          </div>
          <div className="col-md-6 text-md-right">
            <button className="btn btn-primary" style={{ backgroundColor: '#ff6b35', borderColor: '#ff6b35' }} onClick={openNew}>
              <i className="fas fa-plus mr-2"></i>Create Pay Period
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="card mb-4 p-3" style={{ borderRadius: '16px', border: 'none', boxShadow: '0 2px 10px rgba(0,0,0,0.08)' }}>
          <div className="row align-items-end">
            <div className="col-md-3">
              <label className="small text-muted mb-1">From</label>
              <input
                type="date"
                className="form-control"
                value={dateFrom}
                onChange={e => setDateFrom(e.target.value)}
              />
            </div>
            <div className="col-md-3">
              <label className="small text-muted mb-1">To</label>
              <input
                type="date"
                className="form-control"
                value={dateTo}
                onChange={e => setDateTo(e.target.value)}
              />
            </div>
            <div className="col-md-3">
              <label className="small text-muted mb-1">Status</label>
              <div>
                <button
                  className={`btn btn-sm mr-1 ${!showClosed ? 'btn-primary' : 'btn-outline-secondary'}`}
                  style={!showClosed ? { backgroundColor: '#ff6b35', borderColor: '#ff6b35' } : {}}
                  onClick={() => setShowClosed(false)}
                >
                  <i className="fas fa-filter mr-1"></i>Open & Processing
                </button>
                <button
                  className={`btn btn-sm ${showClosed ? 'btn-primary' : 'btn-outline-secondary'}`}
                  style={showClosed ? { backgroundColor: '#ff6b35', borderColor: '#ff6b35' } : {}}
                  onClick={() => setShowClosed(true)}
                >
                  <i className="fas fa-list mr-1"></i>All
                </button>
              </div>
            </div>
            <div className="col-md-3 text-md-right">
              <button
                className="btn btn-outline-secondary btn-sm"
                style={{ borderRadius: 8 }}
                onClick={() => {
                  setDateFrom(addDaysISO(-30));
                  setDateTo(addDaysISO(30));
                  setShowClosed(false);
                }}
              >
                <i className="fas fa-undo mr-1"></i>Reset
              </button>
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="row mb-4">
          {[
            { label: 'Total', value: filteredPeriods.length, icon: 'fa-calendar-alt', color: '#007bff' },
            { label: 'Open', value: filteredPeriods.filter(p => p.status === 'open').length, icon: 'fa-lock-open', color: '#28a745' },
            { label: 'Processing', value: filteredPeriods.filter(p => p.status === 'processing').length, icon: 'fa-sync', color: '#ff6b35' },
            { label: 'Closed', value: filteredPeriods.filter(p => p.status === 'closed').length, icon: 'fa-lock', color: '#6c757d' },
          ].map((kpi, idx) => (
            <div key={idx} className="col-md-3 col-6 mb-3">
              <div className="card h-100" style={{ borderRadius: '16px', border: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                <div className="card-body text-center p-2">
                  <div
                    className="d-flex align-items-center justify-content-center mx-auto mb-1"
                    style={{ width: 36, height: 36, borderRadius: 10, background: kpi.color + '15' }}
                  >
                    <i className={`fas ${kpi.icon}`} style={{ color: kpi.color, fontSize: 14 }}></i>
                  </div>
                  <h6 className="font-weight-bold m-0" style={{ fontSize: '0.9rem' }}>{kpi.value}</h6>
                  <small className="text-muted" style={{ fontSize: '0.7rem' }}>{kpi.label}</small>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Periods by Year */}
        {Object.keys(groupedByYear).sort((a, b) => b.localeCompare(a)).map(year => (
          <div key={year} className="mb-4">
            <h5 className="font-weight-bold mb-3">{year}</h5>
            <div className="card" style={{ borderRadius: '16px', border: 'none', boxShadow: '0 2px 10px rgba(0,0,0,0.08)' }}>
              <div className="table-responsive">
                <table className="table table-hover mb-0" style={{ fontSize: '0.9rem' }}>
                  <thead className="thead-light">
                    <tr>
                      <th>Pay Period</th>
                      <th>Duration</th>
                      <th>Pay Date</th>
                      <th>Days</th>
                      <th>Status</th>
                      <th className="text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {groupedByYear[year].map(p => (
                      <tr key={p.id}>
                        <td>
                          <div className="font-weight-bold">{p.payPeriodName}</div>
                          <small className="text-muted">{formatDateDisplay(p.startDate)} – {formatDateDisplay(p.endDate)}</small>
                        </td>
                        <td>
                          <span className="badge badge-light border" style={{ fontSize: '0.8rem' }}>
                            {formatDateDisplay(p.startDate)}
                          </span>
                          <i className="fas fa-arrow-right mx-1 text-muted" style={{ fontSize: '0.7rem' }}></i>
                          <span className="badge badge-light border" style={{ fontSize: '0.8rem' }}>
                            {formatDateDisplay(p.endDate)}
                          </span>
                        </td>
                        <td>{formatDateDisplay(p.payDate)}</td>
                        <td>{getDaysInPeriod(p.startDate, p.endDate)} days</td>
                        <td>{getStatusBadge(p.status)}</td>
                        <td className="text-right">
                          {p.status === 'open' && (
                            <button
                              className="btn btn-sm btn-outline-warning mr-1"
                              style={{ borderRadius: 8 }}
                              onClick={() => handleClosePeriod(p)}
                              title="Close Period"
                            >
                              <i className="fas fa-lock"></i>
                            </button>
                          )}
                          <button className="btn btn-sm btn-outline-primary mr-1" style={{ borderRadius: 8 }} onClick={() => openEdit(p)}>
                            <i className="fas fa-edit"></i>
                          </button>
                          <button className="btn btn-sm btn-outline-danger" style={{ borderRadius: 8 }} onClick={() => handleDelete(p)}>
                            <i className="fas fa-trash"></i>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        ))}

        {filteredPeriods.length === 0 && (
          <div className="text-center py-5">
            <i className="fas fa-calendar-alt fa-4x text-muted mb-4"></i>
            <h4 className="text-muted">No pay periods found</h4>
            <p className="text-muted">
              {periods.length > 0
                ? 'Try adjusting the date range or status filters'
                : 'Create pay periods to track timesheets and payroll cycles'}
            </p>
          </div>
        )}

        {/* Modal */}
        {showModal && (
          <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content" style={{ borderRadius: 16 }}>
                <div className="modal-header border-0">
                  <h5 className="modal-title font-weight-bold">{editingPeriod ? 'Edit Pay Period' : 'Create Pay Period'}</h5>
                  <button type="button" className="close" onClick={() => { setShowModal(false); resetForm(); }}>
                    <span>&times;</span>
                  </button>
                </div>
                <div className="modal-body">
                  <div className="form-group">
                    <label>Location <span className="text-danger">*</span></label>
                    <select
                      className="form-control"
                      value={formData.locationId}
                      onChange={e => setFormData({ ...formData, locationId: e.target.value })}
                    >
                      <option value="">Select Location</option>
                      {locations.map(loc => (
                        <option key={loc.id} value={loc.id}>{loc.companyName || loc.locationName}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Pay Period Name <span className="text-danger">*</span></label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.payPeriodName}
                      onChange={e => setFormData({ ...formData, payPeriodName: e.target.value })}
                      placeholder="e.g. May 2026 Week 1"
                    />
                  </div>
                  <div className="row">
                    <div className="col-6">
                      <div className="form-group">
                        <label>Start Date <span className="text-danger">*</span></label>
                        <input
                          type="date"
                          className="form-control"
                          value={formData.startDate}
                          onChange={e => setFormData({ ...formData, startDate: e.target.value })}
                        />
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="form-group">
                        <label>End Date <span className="text-danger">*</span></label>
                        <input
                          type="date"
                          className="form-control"
                          value={formData.endDate}
                          onChange={e => setFormData({ ...formData, endDate: e.target.value })}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Pay Date</label>
                    <input
                      type="date"
                      className="form-control"
                      value={formData.payDate}
                      onChange={e => setFormData({ ...formData, payDate: e.target.value })}
                    />
                    <small className="form-text text-muted">The date employees receive payment. Defaults to 2 days after end date.</small>
                  </div>
                  {!editingPeriod && (
                    <>
                      <div className="form-group">
                        <div className="custom-control custom-checkbox">
                          <input
                            type="checkbox"
                            className="custom-control-input"
                            id="recurring"
                            checked={formData.recurring}
                            onChange={e => setFormData({ ...formData, recurring: e.target.checked })}
                          />
                          <label className="custom-control-label" htmlFor="recurring">
                            Generate multiple periods automatically
                          </label>
                        </div>
                      </div>

                      {formData.recurring && (
                        <div className="card p-3 mb-3" style={{ borderRadius: 12, background: '#f8f9fa', border: 'none' }}>
                          <h6 className="font-weight-bold mb-3">Recurrence Settings</h6>
                          <div className="form-group">
                            <label>Recurrence Type</label>
                            <select
                              className="form-control"
                              value={formData.recurrenceType}
                              onChange={e => setFormData({ ...formData, recurrenceType: e.target.value })}
                            >
                              {RECURRENCE_TYPES.map(t => (
                                <option key={t.key} value={t.key}>{t.label}</option>
                              ))}
                            </select>
                          </div>

                          {formData.recurrenceType === 'custom' && (
                            <div className="form-group">
                              <label>Every X Days</label>
                              <input
                                type="number"
                                min={1}
                                max={365}
                                className="form-control"
                                value={formData.customDays}
                                onChange={e => setFormData({ ...formData, customDays: parseInt(e.target.value) || 1 })}
                              />
                            </div>
                          )}

                          {formData.recurrenceType === 'semimonthly' && (
                            <div className="form-group">
                              <label>Pay Schedule</label>
                              <select
                                className="form-control"
                                value={formData.semiMonthlyOption}
                                onChange={e => setFormData({ ...formData, semiMonthlyOption: parseInt(e.target.value) })}
                              >
                                {SEMI_MONTHLY_OPTIONS.map((opt, idx) => (
                                  <option key={idx} value={idx}>{opt.label}</option>
                                ))}
                              </select>
                              <small className="form-text text-muted">
                                Two pay periods will be generated per month based on these dates.
                              </small>
                            </div>
                          )}

                          <div className="form-group mb-0">
                            <label>Number of Periods to Generate</label>
                            <input
                              type="number"
                              min={1}
                              max={52}
                              className="form-control"
                              value={formData.recurrenceCount}
                              onChange={e => setFormData({ ...formData, recurrenceCount: parseInt(e.target.value) || 1 })}
                            />
                            <small className="form-text text-muted">
                              For semi-monthly, this generates pairs of periods.
                            </small>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                  <div className="form-group">
                    <label>Status</label>
                    <select
                      className="form-control"
                      value={formData.status}
                      onChange={e => setFormData({ ...formData, status: e.target.value })}
                    >
                      {STATUS_OPTIONS.map(s => (
                        <option key={s.key} value={s.key}>{s.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="modal-footer border-0">
                  <button className="btn btn-outline-secondary" style={{ borderRadius: 8 }} onClick={() => { setShowModal(false); resetForm(); }}>
                    Cancel
                  </button>
                  <button
                    className="btn btn-primary"
                    style={{ backgroundColor: '#ff6b35', borderColor: '#ff6b35', borderRadius: 8 }}
                    onClick={handleSave}
                    disabled={generating}
                  >
                    {generating ? (
                      <><i className="fas fa-spinner fa-spin mr-2"></i>Generating...</>
                    ) : editingPeriod ? (
                      'Update'
                    ) : (
                      'Create'
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
    </SetupGuard>
  );

  if (embedded) return content;
  return (
    <AdminLayout title="Pay Periods">
      {content}
    </AdminLayout>
  );
};

export default PayPeriodsPage;
